import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { call } from '../lib/backbarApi';
import { ConfirmButton, Empty, ErrorNote, Field, Note, PageHead, Section } from '../components/ui';
import { ago, day, paperworkWords, planWords, roleWords, subStatusWords } from '../lib/format';

type Grant = { id: string; entitlement: string; starts_at: string; ends_at: string | null; reason: string; revoked_at: string | null };
type Sub = { product_id: string | null; entitlement: string; status: string; store: string | null; expires_at: string | null } | null;

type SearchHit = {
  stylist_id: string;
  user_id: string | null;
  name: string | null;
  email: string | null;
  role: string | null;
  is_active: boolean | null;
  salon: { id: string; name: string | null } | null;
  last_activity: string | null;
  grants: Grant[];
  subscription: Sub;
};

type Person = {
  user_id: string;
  email: string | null;
  created_at: string | null;
  last_sign_in_at: string | null;
  last_lesson_at: string | null;
  last_hours_at: string | null;
  lessons_completed: number;
  memberships: {
    stylist_id: string;
    role: string | null;
    is_active: boolean | null;
    salon: { id: string; name: string | null; owner_id: string | null; state_code: string | null } | null;
    mentor: { user_id: string; name: string | null; email: string | null } | null;
    license_number: string | null;
    state_code: string | null;
    paperwork: { status: string | null; answered_at: string | null };
    apprentice_license: { number: string | null; state: string | null; received_at: string | null };
  }[];
  apprentices: { id: string; user_id: string | null; name: string | null; email: string | null; is_active: boolean | null; state_paperwork_status: string | null; apprentice_license_number: string | null }[];
  owned_salons: { id: string; name: string | null }[];
  grants: Grant[];
  subscription: Sub;
  invitations: { id: string; status: string; email: string; expires_at: string | null; last_sent_at: string | null }[];
};

type Salon = { id: string; name: string | null; owner_name: string | null };

function displayName(p: { name?: string | null; email?: string | null } | null | undefined): string {
  return p?.name?.trim() || p?.email || 'No name yet';
}

export function People() {
  const [params, setParams] = useSearchParams();
  const selected = params.get('u');
  const [q, setQ] = useState(params.get('q') ?? '');
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<unknown>(null);

  const search = useCallback(async (term: string) => {
    setSearching(true);
    setSearchError(null);
    try {
      const data = await call<{ people: SearchHit[] }>('users.search', { q: term.trim() });
      setHits(data.people);
    } catch (err) {
      setSearchError(err);
      setHits(null);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (!selected) void search(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  if (selected) {
    return <PersonCard userId={selected} onBack={() => setParams(q ? { q } : {})} />;
  }

  return (
    <>
      <PageHead title="People" sub="Find one person, then help them from their card." />
      <form
        className="lp-panel"
        onSubmit={(e) => {
          e.preventDefault();
          setParams(q ? { q } : {});
          void search(q);
        }}
      >
        <div className="lp-row">
          <input
            className="lp-inp lp-inp--search"
            style={{ flex: 1, minWidth: 240 }}
            placeholder="Name or email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
          <button className="lp-btn lp-btn--primary" type="submit" disabled={searching}>
            {searching ? 'Looking…' : 'Search'}
          </button>
        </div>
      </form>

      <ErrorNote error={searchError} />

      <section className="lp-panel" style={{ marginTop: 14, padding: '6px 24px' }}>
        {!hits && !searchError && <Empty>{searching ? 'Looking…' : 'Type a name or email and tap Search.'}</Empty>}
        {hits && hits.length === 0 && <Empty>Nobody matches that.</Empty>}
        {hits && hits.length > 0 && (
          <ul className="lp-list">
            {hits.map((h) => (
              <li key={h.stylist_id} style={{ display: 'contents' }}>
                <button
                  type="button"
                  className="lp-item lp-item--button"
                  disabled={!h.user_id}
                  onClick={() => h.user_id && setParams({ u: h.user_id, ...(q ? { q } : {}) })}
                >
                  <div className="lp-item-main">
                    <div className="lp-item-title">{displayName(h)}</div>
                    <div className="lp-item-line">
                      {roleWords(h.role)}
                      {h.salon?.name ? ` · ${h.salon.name}` : ''}
                      {h.email ? ` · ${h.email}` : ''}
                      {h.is_active === false ? ' · deactivated' : ''}
                    </div>
                  </div>
                  <div className="lp-item-line">Last active {ago(h.last_activity)}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function accessLine(p: Person): string {
  const sub = p.subscription;
  const grant = p.grants.find((g) => !g.revoked_at && (!g.ends_at || Date.parse(g.ends_at) > Date.now()));
  const parts: string[] = [];
  if (sub && sub.status !== 'expired') {
    parts.push(`${planWords(sub.entitlement)} through the App Store · ${subStatusWords(sub.status)}${sub.expires_at ? `, renews or ends ${day(sub.expires_at)}` : ''}`);
  }
  if (grant) {
    parts.push(`Free access to ${planWords(grant.entitlement)}${grant.ends_at ? ` until ${day(grant.ends_at)}` : ' with no end date'}`);
  }
  if (parts.length) return parts.join(' · ');
  const isApprentice = p.memberships.some((m) => m.role === 'apprentice');
  const isMentor = p.memberships.some((m) => m.role === 'owner' || m.role === 'educator') || p.owned_salons.length > 0;
  if (isApprentice) return 'No plan of their own. Apprentices are included with their mentor’s plan, or with the salon’s plan.';
  if (isMentor) return 'No paid plan. A mentor on a salon plan is included with the salon.';
  return 'No plan.';
}

function PersonCard({ userId, onBack }: { userId: string; onBack: () => void }) {
  const [p, setP] = useState<Person | null>(null);
  const [stylistName, setStylistName] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);
  const [temp, setTemp] = useState<{ password: string; email: string | null } | null>(null);
  const [moving, setMoving] = useState(false);
  const [salons, setSalons] = useState<Salon[] | null>(null);
  const [moveTo, setMoveTo] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [typed, setTyped] = useState('');
  const [deleted, setDeleted] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      // users.get carries no display name at the top level; the stylists row does.
      const [person, nameRow] = await Promise.all([
        call<Person>('users.get', { user_id: userId }),
        supabase.from('stylists').select('name').eq('user_id', userId).not('name', 'is', null).limit(1).maybeSingle(),
      ]);
      setP(person);
      setStylistName((nameRow.data as { name: string | null } | null)?.name ?? null);
    } catch (err) {
      setLoadError(err);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(label: string, fn: () => Promise<void>) {
    setActionError(null);
    setDone(null);
    try {
      await fn();
      setDone(label);
      await load();
    } catch (err) {
      setActionError(err);
    }
  }

  async function loadSalons() {
    setMoving(true);
    if (salons) return;
    const { data: s } = await supabase.from('salons').select('id, name, owner_id').order('name');
    const ownerIds = [...new Set((s ?? []).map((x) => x.owner_id).filter(Boolean))] as string[];
    const { data: owners } = ownerIds.length
      ? await supabase.from('stylists').select('user_id, name').in('user_id', ownerIds)
      : { data: [] as { user_id: string; name: string | null }[] };
    const ownerName = new Map((owners ?? []).map((o) => [o.user_id, o.name]));
    setSalons(
      (s ?? []).map((x) => ({ id: x.id, name: x.name, owner_name: x.owner_id ? ownerName.get(x.owner_id) ?? null : null })),
    );
  }

  if (deleted) {
    return (
      <>
        <PageHead title="Account deleted" back="/people" />
        <Note tone="good">The account and its data are gone. The record of the deletion is kept.</Note>
        <button type="button" className="lp-btn" onClick={onBack}>Back to People</button>
      </>
    );
  }

  if (loadError) {
    return (
      <>
        <PageHead title="Person" back="/people" />
        <ErrorNote error={loadError} />
      </>
    );
  }
  if (!p) {
    return (
      <>
        <PageHead title="Person" back="/people" />
        <Empty>Loading…</Empty>
      </>
    );
  }

  const primary = p.memberships[0] ?? null;
  const shownName = displayName({ name: stylistName, email: p.email });
  const isApprentice = p.memberships.some((m) => m.role === 'apprentice');
  const active = p.memberships.some((m) => m.is_active !== false);
  const openInvite = p.invitations.find((i) => i.status === 'pending' || i.status === 'sent' || i.status === 'open');
  const currentSalonId = p.memberships.find((m) => m.role === 'apprentice')?.salon?.id ?? null;
  const lastActive = [p.last_sign_in_at, p.last_lesson_at, p.last_hours_at].filter(Boolean).sort().pop() ?? null;

  return (
    <>
      <PageHead
        title={shownName}
        back="/people"
        sub={
          <>
            {p.memberships.map((m) => roleWords(m.role)).filter((v, i, a) => a.indexOf(v) === i).join(' and ') || 'No role yet'}
            {primary?.salon?.name ? ` at ${primary.salon.name}` : ''}
            {active ? '' : ' · deactivated'}
          </>
        }
      />

      {done && <Note tone="good">{done}</Note>}
      <ErrorNote error={actionError} notConfigured="Email is not connected yet, so nothing was sent. Set a temporary password instead." />

      <section className="lp-panel">
        <dl className="lp-kv">
          <dt>Email</dt><dd>{p.email ?? '—'}</dd>
          <dt>Last active</dt><dd>{ago(lastActive)}{lastActive ? ` (${day(lastActive)})` : ''}</dd>
          <dt>Joined</dt><dd>{day(p.created_at)}</dd>
          <dt>Lessons finished</dt><dd className="lp-money">{p.lessons_completed}</dd>
          <dt>Access</dt><dd>{accessLine(p)}</dd>
          {p.memberships.map((m) => (
            <MembershipRows key={m.stylist_id} m={m} />
          ))}
        </dl>
      </section>

      {p.grants.length > 0 && (
        <Section title="Free access on record">
          <div className="lp-panel" style={{ padding: '6px 24px' }}>
            <ul className="lp-list">
              {p.grants.map((g) => (
                <li key={g.id} className="lp-item">
                  <div className="lp-item-main">
                    <div className="lp-item-title">{planWords(g.entitlement)}</div>
                    <div className="lp-item-line">
                      {g.revoked_at ? `Ended ${day(g.revoked_at)}` : g.ends_at ? `Until ${day(g.ends_at)}` : 'No end date'} · {g.reason}
                    </div>
                  </div>
                  <Link className="lp-btn lp-btn--small" to="/money">Manage on Money</Link>
                </li>
              ))}
            </ul>
          </div>
        </Section>
      )}

      {isApprentice && p.memberships.some((m) => m.mentor) && (
        <Section title="Mentor">
          <div className="lp-panel">
            {p.memberships.filter((m) => m.mentor).map((m) => (
              <div key={m.stylist_id} className="lp-item">
                <div className="lp-item-main">
                  <div className="lp-item-title">{displayName(m.mentor)}</div>
                  <div className="lp-item-line">{m.salon?.name ?? ''}{m.mentor?.email ? ` · ${m.mentor.email}` : ''}</div>
                </div>
                {m.mentor?.user_id && <Link className="lp-btn lp-btn--small" to={`/people?u=${m.mentor.user_id}`}>Open</Link>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {p.apprentices.length > 0 && (
        <Section title={`Apprentices (${p.apprentices.length})`}>
          <div className="lp-panel" style={{ padding: '6px 24px' }}>
            <ul className="lp-list">
              {p.apprentices.map((a) => (
                <li key={a.id} className="lp-item">
                  <div className="lp-item-main">
                    <div className="lp-item-title">{displayName(a)}</div>
                    <div className="lp-item-line">
                      {paperworkWords(a.state_paperwork_status)}
                      {a.apprentice_license_number ? ` · License ${a.apprentice_license_number}` : ''}
                      {a.is_active === false ? ' · deactivated' : ''}
                    </div>
                  </div>
                  {a.user_id && <Link className="lp-btn lp-btn--small" to={`/people?u=${a.user_id}`}>Open</Link>}
                </li>
              ))}
            </ul>
          </div>
        </Section>
      )}

      <Section title="Help them sign in">
        <div className="lp-panel">
          <div className="lp-actions">
            <ConfirmButton
              label="Send a password reset"
              sentence={`This emails ${p.email ?? 'them'} a link to choose a new password.`}
              yes="Yes, send it"
              onConfirm={() =>
                run('The reset email is on its way.', async () => {
                  await call('users.reset_password', { user_id: userId });
                })
              }
            />
            <ConfirmButton
              label="Set a temporary password"
              sentence="This replaces their password with a temporary one that you read to them. They choose a new one at their next sign-in."
              yes="Yes, set one"
              onConfirm={() =>
                run('A temporary password is set.', async () => {
                  const r = await call<{ password: string; email: string | null }>('users.set_temp_password', { user_id: userId });
                  setTemp({ password: r.password, email: r.email });
                })
              }
            />
            {openInvite && (
              <ConfirmButton
                label="Send the invitation again"
                sentence={`This sends the salon invitation to ${openInvite.email} again.`}
                yes="Yes, send it again"
                onConfirm={() =>
                  run('The invitation was sent again.', async () => {
                    await call('users.resend_invite', { invitation_id: openInvite.id });
                  })
                }
              />
            )}
          </div>
          {temp && (
            <div className="lp-note lp-note--good">
              <div><strong>Temporary password for {temp.email ?? shownName}</strong></div>
              <div className="lp-copy">
                <code>{temp.password}</code>
                <button
                  type="button"
                  className="lp-btn lp-btn--small"
                  onClick={() => void navigator.clipboard?.writeText(temp.password)}
                >
                  Copy
                </button>
              </div>
              <div>Read it to them, then close. It will not be shown again.</div>
              <div style={{ marginTop: 10 }}>
                <button type="button" className="lp-btn lp-btn--small" onClick={() => setTemp(null)}>Close</button>
              </div>
            </div>
          )}
        </div>
      </Section>

      <Section title="Access">
        <div className="lp-panel">
          <div className="lp-actions">
            <Link className="lp-btn" to={`/money?grant=${userId}&name=${encodeURIComponent(shownName)}`}>Give free access</Link>
            {isApprentice && (
              <button type="button" className="lp-btn" onClick={() => void loadSalons()} disabled={moving}>
                Move to another mentor
              </button>
            )}
            {active ? (
              <ConfirmButton
                label="Deactivate"
                sentence={`${shownName} will not be able to use the app until you reactivate them. Nothing is deleted.`}
                yes="Yes, deactivate"
                onConfirm={() =>
                  run('The account is deactivated.', async () => {
                    await call('users.deactivate', { user_id: userId });
                  })
                }
              />
            ) : (
              <ConfirmButton
                label="Reactivate"
                sentence={`${shownName} will be able to use the app again.`}
                yes="Yes, reactivate"
                tone="primary"
                onConfirm={() =>
                  run('The account is active again.', async () => {
                    await call('users.reactivate', { user_id: userId });
                  })
                }
              />
            )}
          </div>

          {moving && (
            <div className="lp-form" style={{ marginTop: 16 }}>
              <Field label="New mentor's salon" hint="The apprentice keeps their hours and progress.">
                <select className="lp-sel" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                  <option value="">{salons ? 'Choose a salon' : 'Loading…'}</option>
                  {(salons ?? []).filter((s) => s.id !== currentSalonId).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name ?? 'Unnamed salon'}{s.owner_name ? ` — ${s.owner_name}` : ''}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="lp-row">
                <ConfirmButton
                  label="Move"
                  tone="primary"
                  disabled={!moveTo}
                  sentence={`${shownName} moves to ${salons?.find((s) => s.id === moveTo)?.name ?? 'that salon'} and their new mentor sees them right away.`}
                  yes="Yes, move them"
                  onConfirm={() =>
                    run('Moved.', async () => {
                      await call('users.move_apprentice', { user_id: userId, salon_id: moveTo });
                      setMoving(false);
                      setMoveTo('');
                    })
                  }
                />
                <button type="button" className="lp-btn lp-btn--quiet" onClick={() => setMoving(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </Section>

      <Section title="Delete">
        <div className="lp-panel">
          {!deleting ? (
            <button type="button" className="lp-btn lp-btn--danger" onClick={() => setDeleting(true)}>
              Delete this account
            </button>
          ) : (
            <div className="lp-form">
              <p className="lp-confirm-text">
                Deleting removes {shownName}&rsquo;s sign-in, lessons, hours, and sign-offs for good. Type their name to confirm.
              </p>
              <Field label={`Type ${shownName}`}>
                <input className="lp-inp" value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
              </Field>
              <div className="lp-row">
                <button
                  type="button"
                  className="lp-btn lp-btn--danger"
                  disabled={typed.trim().toLowerCase() !== shownName.trim().toLowerCase()}
                  onClick={() =>
                    run('Deleted.', async () => {
                      await call('users.delete', { user_id: userId });
                      setDeleted(true);
                    })
                  }
                >
                  Delete for good
                </button>
                <button type="button" className="lp-btn lp-btn--quiet" onClick={() => { setDeleting(false); setTyped(''); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </Section>
    </>
  );
}

function MembershipRows({ m }: { m: Person['memberships'][number] }) {
  const isApprentice = m.role === 'apprentice';
  return (
    <>
      {m.salon && (
        <>
          <dt>Salon</dt>
          <dd>{m.salon.name ?? 'Unnamed salon'}{m.salon.state_code ? ` · ${m.salon.state_code}` : ''}</dd>
        </>
      )}
      {isApprentice && (
        <>
          <dt>Paperwork</dt>
          <dd>
            {paperworkWords(m.paperwork.status)}
            {m.paperwork.answered_at ? ` (answered ${day(m.paperwork.answered_at)})` : ''}
          </dd>
          <dt>Permit or license</dt>
          <dd>
            {m.apprentice_license.number
              ? `${m.apprentice_license.number}${m.apprentice_license.state ? ` · ${m.apprentice_license.state}` : ''}${m.apprentice_license.received_at ? ` · received ${day(m.apprentice_license.received_at)}` : ''}`
              : 'Not entered yet'}
          </dd>
        </>
      )}
      {!isApprentice && (
        <>
          <dt>License number</dt>
          <dd>{m.license_number ? `${m.license_number}${m.state_code ? ` · ${m.state_code}` : ''}` : 'Not entered'}</dd>
        </>
      )}
    </>
  );
}

